import { t } from '@/languages/i18n';
import { HookAPI } from 'antd/es/modal/useModal';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useRef, useState } from 'react';

interface UseLeaveConfirmProps {
    modal: HookAPI;
    initialValue?: boolean;
}

export const useLeaveConfirm = ({ modal, initialValue = false }: UseLeaveConfirmProps) => {
    const router = useRouter();


    const [isChange, setIsChange] = useState(initialValue);
    const trapActiveRef = useRef(false);

    const handleBeforeUnload = useCallback((e: BeforeUnloadEvent) => {
        if (!isChange) return;
        e.preventDefault();
        e.returnValue = '';
    }, [isChange]);

    const handleLinkClick = useCallback((e: MouseEvent) => {
        if (!isChange) return;
        const target = (e.target as Element).closest('a');
        if (!target || !target.href || target.target === '_blank' || e.defaultPrevented) return;

        const isInternal = target.href.startsWith(window.location.origin);
        const currentUrlBase = window.location.href.split('#')[0];
        const targetUrlBase = target.href.split('#')[0];

        if (isInternal && currentUrlBase !== targetUrlBase) {
            e.preventDefault();
            e.stopPropagation();

            modal.confirm({
                title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
                content: t("Bạn có những thay đổi chưa lưu. Nếu thoát bây giờ, dữ liệu của bạn sẽ bị mất."),
                okText: t("Thoát và Bỏ qua"),
                cancelText: t("Ở lại trang"),
                okButtonProps: { danger: true },
                onOk: () => {
                    trapActiveRef.current = false;
                    setIsChange(false);
                    const destination = target.getAttribute('href') || target.href;
                    setTimeout(() => router.push(destination), 0);
                }
            });
        }
    }, [isChange, modal, router]);

    const handlePopState = useCallback(() => {
        if (!isChange) return;
        window.history.pushState({ type: 'change-trap' }, '', window.location.href);
        modal.confirm({
            title: t("Cảnh báo: Dữ liệu chưa được lưu!"),
            content: t("Bạn đang quay lại trang trước. Những thay đổi chưa lưu sẽ bị mất."),
            okText: t("Thoát"),
            cancelText: t("Ở lại"),
            onOk: () => {
                trapActiveRef.current = false;
                setIsChange(false);
                window.history.go(-2);
            }
        });
    }, [isChange, modal]);

    const handleCancel = useCallback((fallbackUrl: string) => {
        if (isChange) {
            modal.confirm({
                title: t("Xác nhận thoát"),
                content: t("Bạn có thay đổi chưa lưu. Bạn có chắc chắn muốn rời đi?"),
                okText: t("Rời đi"),
                cancelText: t("Ở lại"),
                okButtonProps: { danger: true },
                onOk: () => {
                    trapActiveRef.current = false;
                    setIsChange(false);
                    router.push(fallbackUrl);
                }
            });
        } else {
            router.push(fallbackUrl);
        }
    }, [isChange, modal, router]);

    useEffect(() => {
        if (isChange) {
            if (window.history.state?.type !== 'change-trap') {
                window.history.pushState({ type: 'change-trap' }, '', window.location.href);
                trapActiveRef.current = true;
            }
            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('popstate', handlePopState);
            document.addEventListener('click', handleLinkClick, true);
            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
                document.removeEventListener('click', handleLinkClick, true);
            };
        } else {
            const handleCleanPopState = () => {
                if (trapActiveRef.current) {
                    trapActiveRef.current = false;
                    window.history.back();
                }
            };
            window.addEventListener('popstate', handleCleanPopState);
            return () => window.removeEventListener('popstate', handleCleanPopState);
        }
    }, [isChange, handleBeforeUnload, handlePopState, handleLinkClick]);


    return {
        isChange,
        setIsChange,
        handleCancel
    };
};